import { PickingInfo } from "@deck.gl/core"
import { IconPortal } from "themes/default-tailwind/plugins/map-icons/IconPortal"
import { deckglEventEmitter } from "features/map/MapElements"
import useMouse from "tools/events/useMouse"
import { useCallback, useEffect, useState } from "react"
import { get } from "nodash"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { incrementNumberStartAt, selectIconTemplate, setActiveTool } from "../tool-registry"
import {
    ShapePack,
    getIconUrl,
    FireAIIconPaths,
    NWCGIconPaths,
    ForestryIconPaths,
    BCWSIconPaths,
    getPackSize,
    getPackPrefix,
    SarcopIconPaths,
    NFPAEmergencyIconPaths,
    NFPAFireServicePaths,
    NFPAGeneralUsePaths,
} from "@nova/component-map-icons"
import { defaultIconColor } from "features/map/layers/icons/constants"
import {
    IconGhostId,
    LineWidth,
    getRotatedIconsHardCodedValues,
    leftBorderWidth,
    navbarHeight,
    topBorderWidth,
} from "@nova/constants"
import { decodeNumberToColor, encodeColorToNumber } from "@nova/serializer/src/client/util"
import { useMapControl } from "features/map/control/useMapControl"
import { createIcon } from "features/pubsub/entityActions/cubes/icon"
import { selectNextIconNumber } from "features/entity-caching/icons/selectors"
import { selectUserID } from "user/profile/selectors"
import { useResizableSidePanelWidth } from "panels/content/side-panel/SidePanel"
import { NumberPack } from "@nova/component-map-icons/src/icon-packs"

const icons = {
    shape: ShapePack,
    numbers: NumberPack,
    ...FireAIIconPaths,
    ...NWCGIconPaths,
    ...ForestryIconPaths,
    ...BCWSIconPaths,
    ...SarcopIconPaths,
    ...NFPAEmergencyIconPaths,
    ...NFPAFireServicePaths,
    ...NFPAGeneralUsePaths,
}

const defaultIcon = "shape.circle"

export const IconTool = () => {
    const dispatch = useAppDispatch()
    const { mouse, setMouseListener, isMouseListening } = useMouse()
    const { unproject } = useMapControl()

    const userId = useAppSelector(selectUserID)

    const nextIconNumber = useAppSelector(selectNextIconNumber)

    const [activeIconKey, setActiveIconKey] = useState<string | undefined>(defaultIcon)
    const [color, setColor] = useState<string | undefined>()
    const iconTemplate = useAppSelector(selectIconTemplate)

    const pack = getPackPrefix(activeIconKey)
    const size = getPackSize(pack)

    useEffect(() => {
        setMouseListener(true)
    }, [setMouseListener])

    useEffect(() => {
        const url = iconTemplate.url

        setColor(decodeNumberToColor(iconTemplate.color))
        if (url === activeIconKey || !url) return

        // Convert the url from "first/last" to "first.last"
        const parts = url.split("/")
        const convertedUrl = parts.join(".")

        setActiveIconKey(convertedUrl)
    }, [iconTemplate, activeIconKey])

    const sidePanelWidth = useResizableSidePanelWidth()
    const isNumber = iconTemplate.url.startsWith("numbers")
    const placeIcon = useCallback(
        async (shiftKey: boolean) => {
            if (activeIconKey && isMouseListening && mouse && userId) {
                const [longitude, latitude] = unproject([
                    mouse.x - sidePanelWidth - leftBorderWidth / 2,
                    mouse.y - navbarHeight - topBorderWidth / 2,
                ])
                const icon = get(icons, activeIconKey) as { name: string; url: string }
                const isNumber = iconTemplate.url.startsWith("numbers")
                dispatch(
                    createIcon({
                        data: {
                            color: color ? encodeColorToNumber(color) : undefined,
                            longitude,
                            latitude,
                            url: icon.url,
                            name:
                                iconTemplate.name === "Point"
                                    ? `Point ${nextIconNumber}`
                                    : iconTemplate.name,
                            createdBy: {
                                userId,
                            },
                            outline: isNumber ? iconTemplate.outline : undefined,
                            width: isNumber ? iconTemplate.width : undefined,
                        },
                    })
                )

                if (!shiftKey && !icon.url.startsWith("number")) {
                    setMouseListener(false)
                    setActiveIconKey(undefined)
                    dispatch(setActiveTool(undefined))
                } else if (icon.url.startsWith("number")) {
                    dispatch(incrementNumberStartAt())
                }
            }
        },
        [
            activeIconKey,
            isMouseListening,
            mouse,
            userId,
            unproject,
            sidePanelWidth,
            dispatch,
            color,
            iconTemplate,
            nextIconNumber,
            setMouseListener,
        ]
    )

    useEffect(() => {
        const onMapClick = (_info: PickingInfo, event: { srcEvent: PointerEvent }) => {
            void placeIcon(event.srcEvent.shiftKey)
        }
        deckglEventEmitter.on("click", onMapClick)

        return () => {
            deckglEventEmitter.off("click", onMapClick)
        }
    }, [placeIcon])

    return (
        <>
            {mouse && activeIconKey && (
                <IconPortal
                    position={{
                        top: (mouse.y as number) - size / 2,
                        left: (mouse.x as number) - size / 2,
                    }}
                    size={size}
                >
                    <svg
                        id={IconGhostId}
                        style={{
                            cursor: `url("/images/cursors/cursor.svg"), auto`,
                            transform: `rotate(${getRotatedIconsHardCodedValues((get(icons, activeIconKey) as { name: string; url: string }).url)}deg)`,
                        }}
                        data-src={getIconUrl(
                            (get(icons, activeIconKey) as { name: string; url: string }).url
                        )}
                        height={size}
                        width={size}
                        stroke={isNumber ? decodeNumberToColor(iconTemplate.outline) : ""}
                        strokeWidth={
                            isNumber
                                ? iconTemplate.width === LineWidth.thick
                                    ? 1
                                    : iconTemplate.width === LineWidth.normal
                                      ? 0.5
                                      : 0
                                : 0
                        }
                        fill={
                            pack === "shape" || pack === "numbers"
                                ? color || defaultIconColor
                                : undefined
                        }
                        key={activeIconKey}
                    />
                </IconPortal>
            )}
        </>
    )
}
